import {
  asGqlData,
  asTrackingPayload,
  asWidgetProps,
  makeWidgetProps,
} from 'Src/types/widget-helpers';
import React, { ComponentProps, useContext, useState, useEffect, useRef } from 'react';
import { Formik } from 'formik';
import cookie from 'js-cookie';
import { useDispatch } from 'react-redux';
import { t } from 'Src/i18n';
import uniq from 'lodash/uniq';
import get from 'lodash/get';
import { B2CAppContext } from 'Context/ReactAppContext';
import { TRACK_EVENT_NAMES, VOUCHER_CODE, VOUCHER_DISCOUNT } from 'Tracking/constants';
import { CUSTOMER_ACCOUNT_GET_VOUCHERS } from 'Utils/request/requestDictionary';
import { useGQL } from 'Utils/graphql-client/use-gql/useGQL';
import { useGQLErrorsHandler } from 'Utils/use-gql-errors-handler/useGQLErrorsHandler';
import { useAuthentication } from 'Src/hooks/use-authentication/useAuthentication';
import { getVouchersQuery } from 'Views/account/queries/account.queries';
import { Icon } from 'Components/frameworks/icon/Icon';
import { ICONS } from 'Components/utils/icon/icons';
import { COLORS } from 'Components/utils/color/colors';
import { Button } from 'Components/frameworks/button/Button';
import { useTrackEvent } from 'Tracking/tracking-api/useTrackEvent';
import {
  applyProposalPromoCode,
  removeProposalPromoCode,
} from 'Store/booking/proposals/proposals.action';
import { PromoCodeForm } from './PromoCodeForm';
import type { PromoCodeStatus } from './PromoCodeForm';
import { PromoCodeModal } from './PromoCodeModal';
import { DiscountsLine } from './DiscountsLine';
const CODE_CIEL = 'CMCOLL';
const CODE_CIEL_2 = 'COLLR4';
type PromoCodeProps = {
  proposalId: string;
  customerId: number;
  discounts: Array<ProposalDiscount>;
};
type PromoCodeParams = {
  promoCode: string;
  partnerId?: string;
  accessCode?: string;
};
export const PromoCode = ({ proposalId, customerId, discounts }: PromoCodeProps) => {
  const { trackClickEvent } = useTrackEvent();
  const dispatch = useDispatch();
  const context = useContext(B2CAppContext);
  const { isLogged } = useAuthentication();
  const initialDiscounts: Array<string> = discounts
    ?.map((discount) => discount.currentProposalCode?.id)
    .filter(Boolean);
  const [codeCiel, setCodeCiel] = useState(null);
  const [promoCodeStatus, setPromoCodeStatus] = useState<PromoCodeStatus | 'SUCCESS'>(null);
  const [vouchers, setVouchers] = useState(initialDiscounts);
  const [selectedVouchers, setSelectedVouchers] = useState(initialDiscounts);
  const formikSetValues = useRef<
    | ((
        values: { isUnfolded: boolean; promoCode: string | null },
        shouldValidate?: boolean,
      ) => void)
    | null
  >(null);
  const shouldDisplayIsUnfolded =
    isLogged || selectedVouchers.length > 0 || (!isLogged && vouchers.length > 0);
  const { data, error } = useGQL({
    query: getVouchersQuery(),
    queryOptions: {
      cacheFront: {
        key: CUSTOMER_ACCOUNT_GET_VOUCHERS,
      },
      requestName: CUSTOMER_ACCOUNT_GET_VOUCHERS,
    },
    cache: false,
    ignore: !isLogged,
  });
  useEffect(() => {
    // filter removed discounts from selectedVouchers
    const filteredSelectedVouchers = selectedVouchers.filter((voucher) =>
      initialDiscounts.includes(voucher),
    );
    setSelectedVouchers(filteredSelectedVouchers);
  }, [discounts]);
  const gqlData = asGqlData<{ vouchers?: Array<{ items: Array<{ advantageCode?: string }> }> }>(
    data,
  );

  useEffect(() => {
    if (gqlData?.vouchers) {
      const codes = [];
      gqlData.vouchers.forEach((voucher) => {
        voucher.items.forEach((item) => {
          if (item.advantageCode) {
            codes.push(item.advantageCode);
          }
        });
      });
      setVouchers(uniq([...vouchers, ...codes]));
    }
  }, [data]);
  useEffect(() => {
    if (vouchers.length > 0) {
      const dicountsValue = discounts.reduce((acc, discount) => {
        const { amount } = discount;
        if (amount && amount.value) {
          return acc + amount.value;
        }
        return acc;
      }, 0);
      cookie.set(VOUCHER_CODE, JSON.stringify(vouchers));
      cookie.set(VOUCHER_DISCOUNT, dicountsValue);
    } else {
      cookie.remove(VOUCHER_CODE);
      cookie.remove(VOUCHER_DISCOUNT);
    }
  }, [vouchers]);
  useGQLErrorsHandler(error, {
    catchAuthError: true,
  });
  const applyPromoCode = async ({ promoCode, partnerId, accessCode }: PromoCodeParams) => {
    const normalizedPromoCode = promoCode.toUpperCase();
    const params = {
      promoCode: normalizedPromoCode,
      partnerId,
      accessCode,
      customerId,
    };
    try {
      setPromoCodeStatus(null);
      await dispatch(applyProposalPromoCode(proposalId, params, context));
      setPromoCodeStatus('SUCCESS');
      trackClickEvent(
        TRACK_EVENT_NAMES.CLICK_SUMMARY,
        asTrackingPayload({
          detail_click: 'validate_promotional_code',
        }),
      );
      setSelectedVouchers([...selectedVouchers, normalizedPromoCode]);
      if (vouchers && !vouchers.includes(normalizedPromoCode)) {
        setVouchers([...vouchers, normalizedPromoCode]);
      }
      formikSetValues.current?.(
        {
          isUnfolded: false,
          promoCode: null,
        },
        false,
      );
    } catch (e) {
      const errorCode = get(e, 'errors[0].errorCode');
      setPromoCodeStatus(errorCode);
    }
  };
  const removePromoCode = async ({ promoCode }: PromoCodeParams) => {
    const normalizedPromoCode = promoCode.toUpperCase();
    const params = {
      promoCode: normalizedPromoCode,
      customerId,
    };
    try {
      setPromoCodeStatus(null);
      await dispatch(removeProposalPromoCode(proposalId, params, context));
      setSelectedVouchers(selectedVouchers.filter((v) => v !== normalizedPromoCode));
    } catch (e) {
      const errorCode = get(e, 'errors[0].errorCode');
      setPromoCodeStatus(errorCode);
    }
  };
  return (
    <div data-testid="PromoCode">
      <div className="rounded-12 bg-pearl p-20">
        <div className="flex items-center gap-10 md:gap-20">
          <Icon
            {...asWidgetProps<ComponentProps<typeof Icon>>({
              name: ICONS.PROMO_CODE,
              width: '1.875rem',
            })}
          />
          {isLogged
            ? t('booking.priceDetails.promoCode.enableOrMoreLabel')
            : t('booking.priceDetails.promoCode.enableLabel')}
        </div>
        {vouchers && vouchers.length > 0 && (
          <div
            data-testid="PromoCode-vouchers"
            className="my-10 grid w-full gap-10 md:flex md:flex-wrap"
          >
            {vouchers.map((voucher) => (
              <Button
                key={voucher}
                {...asWidgetProps<ComponentProps<typeof Button>>({
                  colorBg: selectedVouchers.includes(voucher) ? COLORS.BLACK : COLORS.WHITE,
                  color: selectedVouchers.includes(voucher) ? COLORS.WHITE : COLORS.BLACK,
                  colorBorder: COLORS.BLACK,
                  iconRight: selectedVouchers.includes(voucher)
                    ? {
                        name: ICONS.CROSS,
                      }
                    : null,
                  minWidth: 'auto',
                  onClick: () => {
                    if (selectedVouchers.includes(voucher)) {
                      removePromoCode({
                        promoCode: voucher,
                      });
                    } else {
                      applyPromoCode({
                        promoCode: voucher,
                      });
                    }
                  },
                })}
              >
                {voucher}
              </Button>
            ))}
          </div>
        )}
        <DiscountsLine
          {...asWidgetProps<ComponentProps<typeof DiscountsLine>>({
            discounts: discounts,
          })}
        />
        <Formik
          {...asWidgetProps<ComponentProps<typeof Formik>>({
            validateOnBlur: true,
            initialValues: {
              isUnfolded: !isLogged && selectedVouchers.length === 0 && vouchers.length === 0,
              promoCode: null,
            },
            onSubmit: ({ promoCode }, { setValues }) => {
              formikSetValues.current = setValues;
              if (
                promoCode.toUpperCase() === CODE_CIEL ||
                promoCode.toUpperCase() === CODE_CIEL_2
              ) {
                return setCodeCiel(promoCode);
              }
              applyPromoCode(
                makeWidgetProps<PromoCodeParams & { setValues?: typeof setValues }>({
                  promoCode,
                  setValues,
                }),
              );
            },
          })}
        >
          <PromoCodeForm
            {...asWidgetProps<ComponentProps<typeof PromoCodeForm>>({
              selectedVouchers: selectedVouchers,
              shouldDisplayIsUnfolded: shouldDisplayIsUnfolded,
              promoCodeStatus: promoCodeStatus,
              setPromoCodeStatus: setPromoCodeStatus,
            })}
          />
        </Formik>
      </div>
      {codeCiel && (
        <PromoCodeModal
          {...asWidgetProps<ComponentProps<typeof PromoCodeModal>>({
            codeCiel: codeCiel,
            customerId: customerId,
            setCodeCiel: setCodeCiel,
            applyPromoCode: applyPromoCode,
          })}
        />
      )}
    </div>
  );
};
